<template>
  <div
    :class="[
      'overlay',
      theme === 'dark'
        ? 'overlay--dark'
        : contained
        ? 'overlay--light-contained'
        : 'overlay--light',
    ]"
  >
    <div class="content">
      <p
        v-if="message"
        :class="[
          'message text-base',
          theme === 'dark' ? 'message--dark' : 'text-gray-500',
        ]"
      >
        {{ message }}
      </p>
      <div
        v-if="variant === 'spinner'"
        class="h-32 w-32 shrink-0 animate-spin rounded-full border-t-4 border-b-4 border-primary-purple"
        aria-hidden="true"
      />
      <div v-else :class="['loader', theme === 'dark' ? 'loader--dark' : '']">
        <div class="dot dot1" />
        <div class="dot dot2" />
        <div class="dot dot1" />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
export default defineComponent({
  name: "BarLoader",
  props: {
    /** `spinner` matches LoadingSpinner / authenticated pages (purple ring). `bars` is the legacy three-bar wave. */
    variant: {
      type: String,
      default: "bars",
      validator: (v) => v === "bars" || v === "spinner",
    },
    /** Optional caption above the loader */
    message: {
      type: String,
      default: "",
    },
    /** `dark` matches legacy dark Cognito overlay; prefer `light` with `contained` on light shells. */
    theme: {
      type: String,
      default: "light",
      validator: (v) => v === "light" || v === "dark",
    },
    /** When true, overlay fills the positioned parent (e.g. CognitoBrandedShell main) instead of the full viewport. */
    contained: {
      type: Boolean,
      default: false,
    },
  },
});
</script>

<style scoped>
.overlay {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Full viewport (default) — e.g. SignInLanding, HomeView */

/*
 * Cognito branded pages: fill only the main slot below the header so the logo
 * stays visible and is not dimmed by a full-screen overlay.
 */

/* Light shell: same inset behavior as overlay--dark, with a light scrim */

.content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.message {
  margin: 0;
}

.loader {
  display: flex;
  gap: 7px;
  align-items: flex-end;
}

.dot {
  width: 3px;
  border-radius: 1px;
  animation: wave 0.6s infinite ease-in-out;
  transform-origin: center center;
}

/* Individual delays to create wave effect */
.dot1 {
  animation-delay: 0s;
}
.dot2 {
  animation-delay: 0.3s;
}

/* primary-purple #6A48F3 — matches tailwind.config.js */
@keyframes wave {
  0%,
  100% {
    background-color: #e9e4fc;
    height: 4px;
    transform: scaleY(1);
  }
  50% {
    background-color: #6a48f3;
    height: 8px;
    transform: scaleY(2);
  }
}

/* Lavender accent on dark overlay — matches Cognito branded shell */
.loader--dark .dot {
  animation-name: waveDark;
}

@keyframes waveDark {
  0%,
  100% {
    background-color: #6e5fd4;
    height: 4px;
    transform: scaleY(1);
  }
  50% {
    background-color: #c4b5ff;
    height: 6px;
    transform: scaleY(2);
  }
}
</style>
